import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
type Rect = Pick<DOMRect, 'left' | 'right' | 'width' | 'height'>;
const RIGHT_EDGE_THRESHOLD = 24;
const MAX_PANEL_RATIO = 0.9;
const MIN_LAUNCHER_SPACE = 220;
const computeRightDrawerInset = (rects: Rect[], viewportWidth: number): number => {
  let inset = 0;
  for (const r of rects) {
    if (r.width <= 0 || r.height <= 0) continue;
    const rightEdgeGap = viewportWidth - r.right;
    if (rightEdgeGap < 0 || rightEdgeGap > RIGHT_EDGE_THRESHOLD) continue;
    if (r.width >= viewportWidth * MAX_PANEL_RATIO) continue;
    const occupied = viewportWidth - r.left;
    if (occupied > inset) inset = occupied;
  }
  return Math.min(Math.round(inset), Math.max(0, viewportWidth - MIN_LAUNCHER_SPACE));
};
const DRAWER_SELECTOR = '[role="dialog"], [role="alertdialog"]';
type RightDrawer = {
  inset: number;
  isCovering: boolean;
};
const measureRightDrawer = (): RightDrawer => {
  const els = Array.from(document.querySelectorAll<HTMLElement>(DRAWER_SELECTOR)).filter(el => !el.closest('[data-percy-chat-host]'));
  const inset = computeRightDrawerInset(els.map(el => el.getBoundingClientRect()), window.innerWidth);
  // A modal drawer marks the rest of the page `data-aria-hidden` (Radix's hideOthers); a non-modal one doesn't.
  const isCovering = inset > 0 && Array.from(document.body.children).some(child => child.getAttribute('data-aria-hidden') === 'true');
  return {
    inset,
    isCovering
  };
};
const useRightDrawerInset = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      inset: 0,
      isCovering: false
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [drawer, setDrawer] = useState(t0);
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (typeof document === "undefined") {
        return;
      }
      let raf = 0;
      let settleUntil = 0;
      const apply = () => {
        const next = measureRightDrawer();
        setDrawer(prev => prev.inset === next.inset && prev.isCovering === next.isCovering ? prev : next);
      };
      const loop = () => {
        apply();
        raf = performance.now() < settleUntil ? requestAnimationFrame(loop) : 0;
      };
      const schedule = () => {
        settleUntil = performance.now() + 350;
        if (!raf) {
          raf = requestAnimationFrame(loop);
        }
      };
      apply();
      const mo = new MutationObserver(schedule);
      mo.observe(document.body, {
        childList: true,
        subtree: true
      });
      window.addEventListener("resize", schedule);
      return () => {
        mo.disconnect();
        window.removeEventListener("resize", schedule);
        if (raf) {
          cancelAnimationFrame(raf);
        }
      };
    };
    t2 = [];
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  return drawer;
};
export default useRightDrawerInset;